import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { formatApiErrorDetail } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Flower } from 'iconoir-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-page flex" data-testid="forgot-password-page">
      <div className="hidden lg:flex lg:w-1/2 relative">
        <img
          src="https://images.unsplash.com/photo-1735052712464-9d24b69be5f5?w=1600&q=80&auto=format&fit=crop"
          alt="South Asian bride and groom walking together"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-charcoal/20" />
      </div>

      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 lg:p-16">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-3 mb-12">
            <Flower className="w-8 h-8 text-terracotta" strokeWidth={1.5} />
            <span className="font-brand text-2xl text-charcoal">Shaadi Planner</span>
          </div>

          <h1 className="font-serif font-light text-4xl sm:text-5xl text-charcoal tracking-tight mb-4">
            Forgot password
          </h1>
          <p className="text-muted font-light mb-10">
            Enter the email on your account and we'll send you a secure link to set a new password.
          </p>

          {sent ? (
            <div
              className="p-6 bg-moss/10 border border-moss/30 rounded-sm text-charcoal"
              data-testid="forgot-password-success"
            >
              <p className="font-sans text-sm mb-2">
                If <strong>{email}</strong> is registered, we just sent a reset link.
              </p>
              <p className="text-muted text-sm">
                Check your inbox (and spam folder). The link expires in 60 minutes.
              </p>
              <div className="mt-6">
                <Link
                  to="/login"
                  className="text-terracotta hover:text-terracotta-dark transition-colors text-sm"
                  data-testid="forgot-back-to-login"
                >
                  ← Back to sign in
                </Link>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              {error && (
                <div
                  className="p-4 bg-terracotta/10 border border-terracotta/30 rounded-sm text-terracotta text-sm"
                  data-testid="forgot-password-error"
                >
                  {error}
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="email" className="text-charcoal font-sans text-sm uppercase tracking-wider">
                  Email
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input-field h-12"
                  placeholder="your@email.com"
                  required
                  data-testid="forgot-password-email-input"
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="btn-primary w-full h-12"
                data-testid="forgot-password-submit-btn"
              >
                {loading ? 'Sending...' : 'Send reset link'}
              </Button>

              <div className="text-center">
                <Link
                  to="/login"
                  className="text-sm text-muted hover:text-terracotta transition-colors"
                  data-testid="forgot-back-to-login-link"
                >
                  ← Back to sign in
                </Link>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
